import {
  RemoteDeviceVersion,
  VersionInfo,
  VersionType,
  DeviceType,
} from '../../../../types/firmware'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { FirmwareFeature } from '../../../../features/firmware/contract'
import { checkACK, DONGLE_MODEL_MAP, versionFileName } from '../../capabilities/memory'
import { CMD_LM20_RT, PROTOCOL_CONFIG_LM20_RT } from '../../protocols/cmd'
import { NordicH20Upgrader } from '../../../shared/firmware/nordic/NordicH20Upgrader'
import { WCHUpgrader } from '../../../shared/firmware/wch/WCHUpgrader'
import { NordicLFirmwareUpgrade, SlotInfo } from '../../../shared/firmware/nordic/NordicUpdater'
import { processZip, FileEntry } from '../../../shared/firmware/shared/FileUtil'
import { BaseDevice } from '../../../../runtime/BaseDevice'
import { delay } from '../../../../utils/TimeUtils'
import { usageType } from '../../../../types/manifest'
import { ConnectionType } from '../../../../types/connection'
import { FirmwareEvents } from '../../../../../src/events/FirmwareEvents'
import { NordicLM20Upgrader } from '../../../shared/firmware/nordic/NordicLM20Upgrader'
import { formatCompactFirmwareVersion } from '../../../../utils/verify'
import { isBaseDockContext } from '../../../../types/base'

const REPORT_ID = PROTOCOL_CONFIG_LM20_RT.report_id
const FEATURE_REPORT_ID = PROTOCOL_CONFIG_LM20_RT.feature_report_id

/** 判断是否为 WebHID HIDDevice 对象 */
function isHIDDevice(device: unknown): device is HIDDevice {
  return (
    device !== null &&
    typeof device === 'object' &&
    'addEventListener' in device &&
    'opened' in device &&
    'sendFeatureReport' in device &&
    'receiveFeatureReport' in device
  )
}

export class LM20RTFirmware extends FirmwareFeature {
  /** 进度缩放配置：当同时升级 WCH 和 Nordic 时，用于将各段 0-100% 原始进度映射到总进度的对应区间 */
  private progressScale?: { offset: number; range: number }

  private async getVersion(
    deviceSelect = 0,
    deviceType = DeviceType.Mouse,
  ): Promise<{ version: string; chipID?: number }> {
    const cmd =
      deviceType === DeviceType.Receiver
        ? CMD_LM20_RT.firmware_version[1]
        : CMD_LM20_RT.firmware_version[0]
    let payload = new Uint8Array([this.device.connectionType, cmd, deviceSelect])
    if (deviceType === DeviceType.Receiver) {
      payload = new Uint8Array([cmd, deviceSelect])
    }
    const res = await this.device.sendAndWait(
      REPORT_ID,
      payload,
      FEATURE_REPORT_ID,
      (res) => res.getUint8(0) !== 0x02,
    )
    checkACK(res)
    const version = res.getUint32(1, true)
    const chipID = res.getUint8(7)
    const sensorID = res.getUint8(8)

    if (
      deviceType === DeviceType.Receiver &&
      chipID === 5 &&
      sensorID === 0 &&
      !isBaseDockContext(this.device)
    ) {
      return {
        version: formatCompactFirmwareVersion(version, -1),
        chipID,
      }
    }

    return {
      version: formatCompactFirmwareVersion(version),
      chipID,
    }
  }
  async getFirmwareVersion(): Promise<VersionInfo[]> {
    const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
    const { version: wchVersion, chipID: wchChipID } = await this.getVersion(0, DeviceType.Mouse)
    const { version: nrfVersion, chipID: nrfChipID } = await this.getVersion(1, DeviceType.Mouse)
    const versions: VersionInfo[] = [
      {
        version: wchVersion,
        versionType: VersionType.USB,
        deviceType: DeviceType.Mouse,
        chipID: wchChipID,
      },
      {
        version: nrfVersion,
        versionType: VersionType.MasterControl,
        deviceType: DeviceType.Mouse,
        chipID: nrfChipID,
      },
    ]
    if (isWireless) {
      const { version: receiverWchVersion, chipID: receiverWchChipID } = await this.getVersion(
        0,
        DeviceType.Receiver,
      )
      const { version: receiverNrfVersion, chipID: receiverNrfChipID } = await this.getVersion(
        1,
        DeviceType.Receiver,
      )
      versions.push(
        ...[
          {
            version: receiverWchVersion,
            versionType: VersionType.USB,
            deviceType: DeviceType.Receiver,
            chipID: receiverWchChipID,
          },
          {
            version: receiverNrfVersion,
            versionType: VersionType.MasterControl,
            deviceType: DeviceType.Receiver,
            chipID: receiverNrfChipID,
          },
        ],
      )
    }
    return versions
  }
  private async getVersionInfo(model: string): Promise<RemoteDeviceVersion> {
    // todo 本地/测试环境开发使用
    if (import.meta.env.VITE_BUILD_FLAVOR === 'test') {
      this.device.__setFirmwareBaseUrl('https://soft.rapoo.com/RapooDriver/FirmwareTest')
    }

    const timestamp = Date.now()
    const versionUrl = `${this.device.getFirmwareBaseUrl()}/${model}/${versionFileName}?t=${timestamp}`
    const versionRes = await fetch(versionUrl)
    if (!versionRes.ok) {
      throw new DeviceError(DeviceErrorCode.TIMEOUT, 'checkFirmwareUpdate failed')
    }
    const versionData = await versionRes.json()
    if (!versionData.Device || !versionData.Device.length) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'checkFirmwareUpdate failed')
    }
    return versionData.Device[0]
  }

  /**
   * 解析当前升级应使用的 model。
   * - 非无线模式：直接取 mainControl，不考虑 chipID（chipID 仅用于无线模式下的 Dongle 匹配）
   * - 无线模式：用设备返回的 chipID 在映射表里查找；命中则取匹配到的 Dongle，否则回退到配置的 dongleModel
   * 无线要解析的原因：部分同型号鼠标的接收器，有的发货L15，有的发货L05
   */
  private resolveModel(chipID: number | undefined): string | undefined {
    const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
    if (!isWireless) {
      return this.device.manifest.mainControl
    }
    return (
      (chipID !== undefined ? DONGLE_MODEL_MAP[chipID] : undefined) ??
      this.device.manifest.dongleModel
    )
  }

  /** 从版本列表中提取接收器 chipID（无线模式用于匹配 Dongle） */
  private pickReceiverChipID(versions: VersionInfo[]): number | undefined {
    const receiver = versions.find(
      (v) => v.deviceType === DeviceType.Receiver && v.versionType === VersionType.MasterControl,
    )
    return receiver?.chipID as number | undefined
  }

  // 对getVersionInfo获取到的版本列表进行可更新判断
  async checkFirmwareUpdate(): Promise<VersionInfo[]> {
    const versions = await this.getFirmwareVersion()

    const model = this.device.manifest.mainControl ?? ''
    const versionFileInfo = await this.getVersionInfo(model)
    // 无线模式下，接收器版本清单应基于设备实际返回 chipID 匹配到的 Dongle，而非直接用配置的 dongleModel
    const receiverModel = this.resolveModel(this.pickReceiverChipID(versions)) ?? ''
    const receiverVersionFileInfo = receiverModel ? await this.getVersionInfo(receiverModel) : null
    const MCU = this.device.manifest?.MCU
    if (!MCU) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED_VERSION,
        'Update firmware failed: MCU not found',
      )
    }

    // Firmware是对于软件端来说的主控，MasterControl是对于固件来说的主控
    // 所以这里USB放Firmware，MasterControl反而放Transparent
    // N54H20只有一个芯片，所以这里直接MasterControl放Firmware
    return versions.map((current) => {
      const v = { ...current }
      v.canUpdate = false
      // 鼠标本体升级比较
      if (v.deviceType === DeviceType.Mouse) {
        const { FirmwareVersion, TransparentVersion } = versionFileInfo
        if (MCU === 'N54H20') {
          // 注意：N54H20 固件升级只有nrf54芯片需要升级，而且升级包放在FirmwareUrl下面与其他54系列相反
          if (v.versionType === VersionType.MasterControl) {
            v.onlineVersion = FirmwareVersion
            v.canUpdate = this.canUpdateByVersion(FirmwareVersion, v.version)
          }
        } else {
          if (v.versionType === VersionType.USB) {
            v.onlineVersion = FirmwareVersion
            v.canUpdate = this.canUpdateByVersion(FirmwareVersion, v.version)
          }
          if (v.versionType === VersionType.MasterControl) {
            v.onlineVersion = TransparentVersion
            v.canUpdate = this.canUpdateByVersion(TransparentVersion, v.version)
          }
        }
      }
      // 接收器升级比较
      if (v.deviceType === DeviceType.Receiver && receiverVersionFileInfo) {
        const { FirmwareVersion, TransparentVersion } = receiverVersionFileInfo
        if (v.versionType === VersionType.USB) {
          v.onlineVersion = FirmwareVersion
          v.canUpdate = this.canUpdateByVersion(FirmwareVersion, v.version)
        }
        if (v.versionType === VersionType.MasterControl) {
          v.onlineVersion = TransparentVersion
          v.canUpdate = this.canUpdateByVersion(TransparentVersion, v.version)
        }
      }
      return v
    })
  }
  /**
   * nordic 固件升级
   *
   * 多阶段升级时收集临时设备，全部结束后一次性 release，避免 WCH→Nordic
   * 中间 close 触发接收器复位/重枚举（与底座 RapooBaseFirmware 一致）。
   */
  async updateFirmware(): Promise<void> {
    // 收集所有升级用到的临时设备，在所有阶段完成后一次性关闭。
    const openedDevices: BaseDevice[] = []

    try {
      const MCU = this.device.manifest?.MCU
      if (!MCU) {
        throw new DeviceError(
          DeviceErrorCode.NOT_SUPPORTED_VERSION,
          'Update firmware failed: MCU not found',
        )
      }

      const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
      // 远程版本信息应基于“设备实际对应的 Dongle/主控”获取：
      // - 无线：以设备返回的 chipID 匹配 Dongle，命中则用该 Dongle，否则回退 dongleModel
      // - 有线：直接用 mainControl，chipID 仅用于无线模式下的 Dongle 匹配
      const versions = await this.getFirmwareVersion()
      console.log('versions', versions)
      const model = this.resolveModel(this.pickReceiverChipID(versions))
      if (!model) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
      }
      const { FirmwareURL, TransparentURL } = await this.getVersionInfo(model)
      console.log('FirmwareURL', FirmwareURL)
      console.log('TransparentURL', TransparentURL)

      if (MCU === 'N54H20') {
        if (!FirmwareURL) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
        }
        // 注意：H20直接升级nordic就行，但是H20的nordic芯片升级包放在FirmwareUrl下面而不是TransparentURL
        console.log('N54H20 固件升级')
        const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
        await this.updateN54H20(url, openedDevices)
      } else if (MCU === 'N54LM20') {
        console.log('N54LM20 固件升级')
        const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
        await this.updateN54LM20(url, openedDevices)
      } else {
        if (!FirmwareURL && !TransparentURL) {
          throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateFirmware failed')
        }
        const updatableVersions = (await this.checkFirmwareUpdate()).filter((v) => {
          if (!v.canUpdate) return false
          if (isWireless && v.deviceType === DeviceType.Receiver) return true
          if (!isWireless && v.deviceType === DeviceType.Mouse) return true
          return false
        })
        // lm20无TransparentURL需要升级，所以这里需要额外判断json文件是否有对应的升级要求
        const hasUSBUpdate =
          updatableVersions.find((v) => v.versionType === VersionType.USB) && FirmwareURL
        const hasMasterControlUpdate =
          updatableVersions.find((v) => v.versionType === VersionType.MasterControl) &&
          TransparentURL
        // 判断是否需要同时升级两个固件，以决定进度缩放
        const needBoth = !!(hasUSBUpdate && hasMasterControlUpdate)
        if (hasUSBUpdate) {
          console.log('wch 沁恒固件升级')
          const url = `${this.device.getFirmwareBaseUrl()}/${model}/${FirmwareURL}`
          // 如果两个固件都需要升级，WCH 进度占 0-50%；否则不缩放
          this.progressScale = needBoth ? { offset: 0, range: 50 } : undefined
          await this.updateFirmwareWCH(url, openedDevices)
        }
        await delay(200)
        if (hasMasterControlUpdate) {
          console.log('N54 系列固件升级')
          const url = `${this.device.getFirmwareBaseUrl()}/${model}/${TransparentURL}`
          // 如果两个固件都需要升级，N54 进度占 50-100%；否则不缩放
          this.progressScale = needBoth ? { offset: 50, range: 50 } : undefined
          await this.updateN54(url, openedDevices)
        }
      }
    } catch (error) {
      console.error(error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, JSON.stringify(error))
    } finally {
      this.progressScale = undefined
      // 所有阶段结束后，一次性关闭所有临时设备，避免中间关闭触发硬件复位
      for (const dev of openedDevices) {
        await this.device.session.channels.releaseDevice(dev)
      }
    }
  }

  /**
   * WCH (沁恒) 固件升级
   *
   * 流程: 擦除Flash → 写入固件(进度 0-50%) → 验证固件(进度 50-100%) → endDeviceUpdate(重启)
   *
   * 注意: 当同时需要升级 WCH 和 Nordic 时, updateFirmware() 会自动设置 progressScale
   *       将本段的原始进度 0-100% 等比映射到总进度的 0-50%。单独升级时则不上报缩放后的进度。
   *       临时设备不在此阶段关闭，由 updateFirmware() finally 统一 release。
   */
  private async updateFirmwareWCH(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const updateDevice = await this.createFirmwareUpdateDevice('updateWCH')
    openedDevices.push(updateDevice)

    const fileMap = await processZip(url)
    const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array
    if (!firmwareData) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'firmwareData is empty')
    }

    const upgrader = new WCHUpgrader(updateDevice)

    // 进入更新模式：接收器走 [0xb1]，鼠标本体走 [0xff, 0xa8]
    const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
    const enterPayload = isWireless ? [0xb1] : [0xff, 0xa8]
    await upgrader.enterUpdateMode(enterPayload)
    // 等待设备切到 Bootloader 并重举，避免紧接着的 startUpdate 抢在切换之前
    await delay(200)

    upgrader.setOnProgressCallback((progress: number) => {
      const p = Math.min(100, Math.max(0, Math.floor(progress * 100) / 100))
      console.log(`WCH 固件升级进度: ${p}%`)
      this.reportProgress(p)
    })

    upgrader.setOnProgressFinishCallback(() => {
      this.reportProgress(100)
      console.log('WCH 固件验证完成')
    })

    await upgrader.startUpdate(firmwareData)

    // 仅单阶段升级时发送 end；双升时留给后续 Nordic，避免中途复位
    if (!this.progressScale) {
      console.log('WCH 固件升级完成, 发送结束命令...')
      await upgrader.endDeviceUpdate()
    }
    console.log('WCH 升级流程全部完成')
  }

  /**
   * N54L15 固件升级：使用 SMP over HID 协议 (MCUboot)
   *
   * 升级流程 (事件驱动, 与旧代码 FirmwareUpdate.vue updateNordic 一致):
   *   1. enterUpdateMode       → 发送 Feature Report 进 入 Bootloader
   *   2. start_image_get       → 发送 SMP Image State READ 查询当前 Slot
   *   3. slotInfoCallback      → 设置 fwUint8Array + updateFwSlot
   *   4. SMP 分片上传递归      → fileUpload 递归直到上传完成
   *   5. updateFinishedCallback → start_reset 重启设备 + resolve Promise
   *
   * 注意: 当同时需要升级 WCH 和 Nordic 时, updateFirmware() 会自动设置 progressScale
   *       将本段的原始进度 0-100% 等比映射到总进度的 50-100%。单独升级时则不上报缩放后的进度。
   *       有线模式打开的 update 临时设备不在此关闭，由 updateFirmware() finally 统一 release。
   */
  private async updateN54(url: string, openedDevices: BaseDevice[]): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const fileMap = await processZip(url)

        const fwUpgrader = new NordicLFirmwareUpgrade()

        let device = this.device.channel.nativeDevice
        const isWireless = this.device.getConnectionType() === ConnectionType.Wireless
        // 无线/接收器模式走 usage:2 default 通道；有线才需要更换通道为 update
        if (!isWireless) {
          const updateDevice = await this.createFirmwareUpdateDevice('update')
          openedDevices.push(updateDevice)
          await delay(200)

          device = updateDevice.channel.nativeDevice
          console.log('Device opened for Nordic upgrade', device)
        }

        // 使用鸭式类型检查替代 instanceof HIDDevice (HIDDevice 是 interface, 不能 instanceof)
        if (!isHIDDevice(device)) {
          throw new DeviceError(
            DeviceErrorCode.NOT_SUPPORTED_VERSION,
            'Update firmware failed: Invalid device type. Only WebHID HIDDevice is supported for N54L15.',
          )
        }

        // 打开设备（如果尚未打开）
        if (!device.opened) {
          await device.open()
          console.log('Device opened for Nordic upgrade')
        }

        fwUpgrader.setSmpErrorCallback((errorMsg: string) => {
          reject(new Error(errorMsg))
        })

        fwUpgrader.setDevice(device)
        this.reportProgress(0)
        // 接收器升级走 [0xb1, 0x01]，设备本体升级走 [0xff, 0xa8, 0x01]
        const enterPayload = isWireless ? [0xb1, 0x01] : [0xff, 0xa8, 0x01]
        await fwUpgrader.enterUpdateMode(enterPayload)
        console.log('N54L15 固件升级进入 Bootloader 模式')

        fwUpgrader.setSlotInfoCallback(async (curSlotInfo: SlotInfo) => {
          console.log('N54L15 固件升级获取到当前 Slot:', curSlotInfo)
          const fwUint8Array = getFwUint8Array(fileMap, curSlotInfo)
          if (!fwUint8Array) {
            console.warn('[FW-DIAG] fileMap 无匹配 slot，升级可能挂起', {
              curSlotInfo,
              fileMapKeys: Object.keys(fileMap),
            })
            return
          }
          fwUpgrader.setFwUint8Array(fwUint8Array)

          const info = await fwUpgrader.updateFwSlot()
          if (info !== null) {
            console.log('升级失败', info)
            reject(new Error(`升级失败: ${info}`))
          }
        })

        fwUpgrader.setUpdateProgressCallback((progress: number) => {
          console.log('N54L15 固件升级进度:', progress)
          const p = Math.min(100, Math.max(0, Math.floor(progress * 100) / 100))
          this.reportProgress(p)
        })

        fwUpgrader.setUpdateFinishedCallback(async () => {
          console.log('N54L15 固件升级完成, 发送重启命令...')
          this.reportProgress(100)
          await fwUpgrader.start_reset()
          resolve()
        })

        await fwUpgrader.start_image_get()
        console.log('N54L15 固件升级开始')
      } catch (error) {
        console.error('N54L15 固件升级失败:', error)
        reject(error)
      }
    })
  }

  /**
   * N54H20 固件升级：使用自定义 HID Feature Report 协议
   *
   * 与 N54L15 的 SMP/MCUboot 标准协议不同，N54H20 采用私有 HID 命令协议进行升级。
   *
   * 升级流程:
   *   Boot倒计时 → 文件信息(大小+CRC32) → 固件分片传输 → 重启
   *
   * 使用 NordicH20Upgrader 完成底层通信和协议细节。
   */
  private async updateN54H20(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const fileMap = await processZip(url)
    const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array
    if (!firmwareData) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'firmwareData is empty')
    }

    const updateDevice = await this.createFirmwareUpdateDevice('update')
    openedDevices.push(updateDevice)

    const upgrader = new NordicH20Upgrader(updateDevice)

    upgrader.setOnProgressCallback((progress: number) => {
      const p = Math.min(100, Math.max(0, Math.floor(progress * 100) / 100))
      this.reportProgress(p)
    })

    upgrader.setOnProgressFinishCallback(() => {
      console.log('N54H20 固件升级完成')
    })

    try {
      await upgrader.startUpdate(firmwareData)
    } catch (error) {
      console.error('N54H20 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `N54H20 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }
  }

  private async updateN54LM20(url: string, openedDevices: BaseDevice[]): Promise<void> {
    const fileMap = await processZip(url)
    const firmwareData = Object.values(fileMap)[0]?.data as Uint8Array
    if (!firmwareData) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'firmwareData is empty')
    }

    const updateDevice = await this.createFirmwareUpdateDevice('update')
    openedDevices.push(updateDevice)

    const upgrader = new NordicLM20Upgrader(updateDevice)

    upgrader.setOnProgressCallback((progress: number) => {
      const p = Math.min(100, Math.max(0, Math.floor(progress * 100) / 100))
      this.reportProgress(p)
    })

    upgrader.setOnProgressFinishCallback(() => {
      console.log('N54LM20 固件升级完成')
    })

    try {
      await upgrader.startUpdate(firmwareData)
    } catch (error) {
      console.error('N54LM20 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `N54LM20 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    }
  }

  /**
   * 上报进度，若存在 progressScale 则按等比缩放
   * @param rawProgress 原始进度 0-100
   */
  private reportProgress(rawProgress: number) {
    let scaledProgress = rawProgress
    if (this.progressScale) {
      // scaled = offset + range * (raw / 100)
      scaledProgress = this.progressScale.offset + this.progressScale.range * (rawProgress / 100)
    }
    this.device.emit(FirmwareEvents.WritedData, { progress: Math.floor(scaledProgress) })
  }

  private async createFirmwareUpdateDevice(usageType: usageType): Promise<BaseDevice> {
    const device = await this.device.session.channels.openDevice({ usageType: usageType })
    if (!device) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'updateDevice is empty')
    }
    return device
  }
}

/**
 * 从解压后的 ZIP 文件映射中，根据当前 Slot 找到要升级的目标固件数据
 *
 * 规则：如果当前 slot=0 则升级 slot1，反之亦然
 * 文件名需包含 "slot1" 或 "slot0" 标识
 */
function getFwUint8Array(
  fileMap: Record<string, FileEntry>,
  curSlotInfo: SlotInfo,
): Uint8Array | null {
  const toUpdateSlot = curSlotInfo.slot === 0 ? 1 : 0
  const fileFeature = `slot${toUpdateSlot}`
  let fwUint8Array: Uint8Array | null = null
  for (const fileName in fileMap) {
    if (fileName.includes(fileFeature)) {
      fwUint8Array = fileMap[fileName].data
    }
  }
  return fwUint8Array
}
